import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { IoArrowForward, IoChevronBack, IoChevronForward } from 'react-icons/io5';
import { useAuth } from 'context/AuthContext';
import { useModal } from 'context/ModalContext/Modal.context';
import { fetchChallengesWithStatus } from 'services/challenge.service';
import { buscarIniciativas } from 'services/iniciativa.service';
import { buscarComunicados } from 'services/comunicado.service';
import { Step } from 'types/steps';
import navesHome from 'assets/img/2026/naves_home.png';
import celularComunicado from 'assets/img/2026/celular_comunicado.png';
import premioElbb from 'assets/img/2026/premio_elbb.png';
import avisoInscricao from 'assets/img/2026/ELBB/aviso_inscricao.png';
import { Header } from './components/Header';
import { Timeline } from './components/Timeline';
import { IniciativaCard } from './components/IniciativaCard';
import { ComunicadoBanner } from './components/ComunicadoBanner';
import { Hero } from './components/Hero';
import { OnboardingModal } from 'components/OnboardingModal';
import './style.scss';
import { getAllTutorials } from 'services/tutorial.service';

export function HomePublic() {
  const { user, stepsList, tutorial } = useAuth();
  const navigate = useNavigate();
  const { showModal, closeModal } = useModal();
  const [badges, setBadges] = useState<any[]>([]);
  const [iniciativas, setIniciativas] = useState<any[]>([]);
  const [comunicados, setComunicados] = useState<any[]>([]);
  const [stepAtivo, setStepAtivo] = useState(0);
  const [isCarouselPaused, setIsCarouselPaused] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [loadingBadges, setLoadingBadges] = useState(true);
  const [loadingIniciativas, setLoadingIniciativas] = useState(true);
  const [loadingComunicados, setLoadingComunicados] = useState(true);

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${day}/${month}`;
  };

  const cronograma = useMemo(() => {
    if (!stepsList || stepsList.length === 0) return [];
    const now = Date.now();
    return stepsList
      .filter((step: Step) => !step._id.includes('apuration'))
      .map((step: Step, index: number) => {
        let status: 'concluido' | 'em_andamento' | 'pendente' = 'pendente';
        if (step.active) {
          status = 'em_andamento';
        } else if (now > step.endDate) {
          status = 'concluido';
        }
        let percentual: number | undefined;
        if (status === 'em_andamento') {
          const totalDuration = step.endDate - step.startDate;
          const elapsed = now - step.startDate;
          percentual = Math.min(Math.round((elapsed / totalDuration) * 100), 100);
        }
        return {
          id: index + 1,
          _id: step._id,
          titulo: step.title,
          descricao: step.description || '',
          dataInicio: formatDate(step.startDate),
          dataFim: formatDate(step.endDate),
          status,
          percentual,
          badge: status === 'em_andamento' ? 'Em andamento' : undefined
        };
      });
  }, [stepsList]);

  const loadingSteps = !stepsList || stepsList.length === 0;

  useEffect(() => {
    async function loadBadges() {
      if (!user?._id) return;
      
      try {
        setLoadingBadges(true);
        const challengesData = await fetchChallengesWithStatus(user._id);
        
        const sortedChallenges = [...challengesData].sort((a, b) => {
          if (a.concluida && !b.concluida) return -1;
          if (!a.concluida && b.concluida) return 1;
          return 0;
        });
        
        const badgesData = sortedChallenges.slice(0, 5).map(challenge => ({
          id: challenge.id,
          nome: challenge.titulo,
          imagem: challenge.icone,
          conquistado: challenge.concluida
        }));
        
        setBadges(badgesData);
      } catch (error) {
        console.error('Erro ao carregar badges:', error);
      } finally {
        setLoadingBadges(false);
      }
    }
    
    loadBadges();
  }, [user?._id]);

  useEffect(() => {
    async function carregarIniciativas() {
      try {
        setLoadingIniciativas(true);
        const data = await buscarIniciativas({
          start: "2026-01-01T00:00:00-03:00",
          userId: user?._id
        });
        setIniciativas(data.slice(0, 3) || []);
      } catch (error) {
        setIniciativas([]);
      } finally {
        setLoadingIniciativas(false);
      }
    }

    if (user?._id) {
      carregarIniciativas();
    }
  }, [user?._id]);

  useEffect(() => {
    async function carregarComunicados() {
      try {
        setLoadingComunicados(true);
        const data = await buscarComunicados();
        setComunicados(data || []);
      } catch (error) {
        console.error('Erro ao carregar comunicados:', error);
        setComunicados([]);
      } finally {
        setLoadingComunicados(false);
      }
    }

    carregarComunicados();
  }, []);

  useEffect(() => {
    async function carregarTutorial() {
      try {
        const data = await getAllTutorials();
        if (data && data.length > 0) {
          const conexaoTutorial = data.find(t => t.title === 'Conexao') ?? data[0];
          // Tutorial is managed by AuthContext
        }
      } catch (error) {
        console.error('Erro ao carregar tutorial:', error);
      }
    }

    carregarTutorial();
  }, []);

  useEffect(() => {
    if (comunicados.length > 0 && comunicados[0].steps.length > 1) {
      const startInterval = () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
        
        if (!isCarouselPaused) {
          intervalRef.current = setInterval(() => {
            setStepAtivo((prev) => {
              const totalSteps = comunicados[0].steps.length;
              return (prev + 1) % totalSteps;
            });
          }, 5000);
        }
      };

      startInterval();

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }
  }, [comunicados, isCarouselPaused]);

  if (!user) return null;

  const getUserData = () => ({
    nome: user.name || 'Usuário',
    primeiraLetra: user.name?.charAt(0).toUpperCase() || 'U',
    foto: user.image?.small?.url
  });

  const parseStepContent = (html: string) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    const h3Element = tempDiv.querySelector('h3');
    const titulo = h3Element?.outerHTML || '';
    
    if (h3Element) {
      h3Element.remove();
    }
    
    let conteudo = tempDiv.innerHTML.trim();
    
    const h1Element = tempDiv.querySelector('h1');
    if (h1Element) {
      conteudo = h1Element.innerHTML;
    }
    
    return { titulo, conteudo };
  };

  const handlePrevStep = () => {
    setStepAtivo((prev) => {
      const totalSteps = comunicados[0].steps.length;
      return prev === 0 ? totalSteps - 1 : prev - 1;
    });
  };

  const handleNextStep = () => {
    setStepAtivo((prev) => {
      const totalSteps = comunicados[0].steps.length;
      return (prev + 1) % totalSteps;
    });
  };

  const handleOpenOnboarding = () => {
    if (tutorial && tutorial.steps.length > 0) {
      showModal(
        'onboarding-modal',
        <OnboardingModal 
          steps={tutorial.steps} 
          onClose={closeModal}
        />,
        'onboarding-modal-wrapper',
        false
      );
    } else {
      console.warn('Tutorial não disponível');
    }
  };

  return (
    <div id="home-public-wrapper">
      <Header 
        currentPage="inicio"
      />

      <Hero 
        user={getUserData()}
        badges={loadingBadges ? [] : badges}
        ilustracao={navesHome}
        onOpenOnboarding={handleOpenOnboarding}
      />

      <main className="home-main">

        {user?.extra?.etapa === 2 && (
          <section className="max-w-[1200px] mx-auto px-4 mt-6">
            <div className="bg-blue-bb-1 rounded-lg p-4 md:p-5 text-white">
              <h2 className="font-title font-bold text-lg md:text-xl mb-1">
                Banca Técnica — Prêmio ELBB
              </h2>
              <p className="text-sm md:text-base opacity-90">
                Você está participando como membro da Banca Técnica. Aguarde as orientações sobre as iniciativas que serão avaliadas.
              </p>
            </div>
          </section>
        )}

        <section className="cronograma-section">
          <h2 className="section-title">Acompanhe o cronograma</h2>
          {loadingSteps ? (
            <div className="flex justify-center items-center py-8">
              <span className="text-blue-bb">Carregando cronograma...</span>
            </div>
          ) : (
            <Timeline steps={cronograma} />
          )}
        </section>

        {/* Notification Banner */}
        <section className="notification-banner">
          <div className="notification-banner__content">
            <img 
              src={avisoInscricao} 
              alt="Aviso" 
              className="notification-banner__icon"
            />
            <p className="notification-banner__text">
              Você possui iniciativas para inscrição e/ou confirmação de participação!
            </p>
            <button 
              className="notification-banner__btn"
              onClick={() => navigate('/iniciativas')}
            >
              VER INICIATIVAS
            </button>
          </div>
        </section>

        <section className="iniciativas-section">
          <div className="iniciativas-section__content">
            <h2 className="section-title">Iniciativas</h2>
            {loadingIniciativas ? (
              <div className="flex justify-center items-center py-8">
                <span className="text-blue-bb">Carregando iniciativas...</span>
              </div>
            ) : (
              <>
                <div className="iniciativas-grid">
                  {iniciativas.map((iniciativa) => (
                    <IniciativaCard
                      key={iniciativa.id}
                      {...iniciativa}
                    />
                  ))}
                </div>
                <button className="btn-ver-todas" onClick={() => navigate('/iniciativas')}>VER TODAS AS INICIATIVAS</button>
              </>
            )}
          </div>
        </section>

        <section className="comunicados-section">
          <h2 className="section-title">Comunicados</h2>
          <div className="comunicados-container">
            {loadingComunicados ? (
              <div className="flex justify-center items-center py-8">
                <span className="text-blue-bb">Carregando comunicados...</span>
              </div>
            ) : comunicados.length > 0 && comunicados[0].steps.length > 0 ? (
              <div 
                className="comunicado-carrossel"
                onMouseEnter={() => setIsCarouselPaused(true)}
                onMouseLeave={() => setIsCarouselPaused(false)}
              >
                <button 
                  className="carousel-arrow carousel-arrow--prev"
                  onClick={handlePrevStep}
                  aria-label="Comunicado anterior"
                >
                  <IoChevronBack size={32} />
                </button>
                
                {(() => {
                  const currentStep = comunicados[0].steps[stepAtivo];
                  const { titulo, conteudo } = parseStepContent(currentStep?.text || '');
                  return (
                    <ComunicadoBanner
                      titulo={titulo}
                      conteudo={conteudo}
                      ilustracao={celularComunicado}
                      ctaTexto="QUERO CONHECER!"
                      onCtaClick={() => console.log('CTA clicked')}
                    />
                  );
                })()}
                
                <button 
                  className="carousel-arrow carousel-arrow--next"
                  onClick={handleNextStep}
                  aria-label="Próximo comunicado"
                >
                  <IoChevronForward size={32} />
                </button>
                
                <div className="carousel-dots">
                  {comunicados[0].steps.map((_: any, index: number) => (
                    <button 
                      key={index}
                      className={`carousel-dot ${index === stepAtivo ? 'active' : ''}`}
                      onClick={() => setStepAtivo(index)}
                      aria-label={`Ver comunicado ${index + 1}`}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <div className="comunicado-carrossel">
                <p className="text-center text-gray-500 py-8">Nenhum comunicado disponível no momento.</p>
              </div>
            )}
            
            <div className="premio-card">
              <div className="premio-card__inner">
                <p className="premio-card__subtitle">Conheça também as</p>
                <p className="premio-card__subtitle">iniciativas do</p>
                <div className="premio-card__logo">
                  <img src={premioElbb} alt="Prêmio ELBB" />
                </div>
                <button className="premio-card__button" onClick={() => navigate('/login-elbb')}>QUERO CONHECER!</button>
              </div>
            </div>
          </div>
        </section>

        <section className="cta-section">
          <div className="cta-card cta-card--yellow" onClick={() => navigate('/iniciativas')} style={{ cursor: 'pointer' }}>
            <div className="cta-card__content">
              <h3 className="cta-card__title">Explorar iniciativas</h3>
              <p className="cta-card__subtitle">Conheça as iniciativas inspiradoras</p>
            </div>
            <button className="cta-card__btn">
              <IoArrowForward size={28} />
            </button>
          </div>
          <div className="cta-card cta-card--pink" onClick={() => navigate('/minha-jornada')} style={{ cursor: 'pointer' }}>
            <div className="cta-card__content">
              <h3 className="cta-card__title">Minha jornada</h3>
              <p className="cta-card__subtitle">Complete desafios e ganhe badges</p>
            </div>
            <button className="cta-card__btn">
              <IoArrowForward size={28} />
            </button>
          </div>
        </section>

        <section className="badges-section">
          <h2 className="section-title">Coleção de badges</h2>
          {loadingBadges ? (
            <div className="flex justify-center items-center py-8">
              <span className="text-blue-bb">Carregando badges...</span>
            </div>
          ) : (
            <div className="badges-container">
              <div className="badges-grid">
                {badges.map((badge) => (
                  <div key={badge.id} className={`badge-item ${badge.conquistado ? 'conquistado' : 'bloqueado'}`}>
                    {badge.conquistado && <span className="badge-check">✓</span>}
                    <img src={badge.imagem} alt={badge.nome} className="badge-image" />
                    <p className="badge-nome">{badge.nome}</p>
                  </div>
                ))}
              </div>
              <button className="btn-ver-todos" onClick={() => navigate('/minha-jornada#badges')}>VER TODOS</button>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
